import { useCallback, useEffect, useRef, useState } from "react";
import FormFlows from "./formFlow";
import { getEntitesFromLibraryUrl } from "@Services/dataManager";
import { libraryEntities, defaultModulesMapper } from "@Helpers";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import { useTranslation } from "@i18n";
import { useDispatch, useSelector } from "react-redux";
import { getUserSelection } from "./formsHelpers";
import { formFlowController } from "@Actions/Forms";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { RootState } from "@Reducers/index";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import { getAllUsersInSpace } from "@Actions/projectUsers";

const FormFlowLibraryPopup = ({
  projectId,
  handleCancelibrary,
  setAddFromLibrary,
  addFromLibrary,
  resourceType,
  refreshGrid,
}: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [spaceId, setSpaceId] = useState("");

  const getQuery = useCallback(() => {
    return {
      entity: libraryEntities.FORM_FLOW,
      where: [
        {
          isComplex: false,
          field: "data.formCategory",
          operator: "equal",
          ignoreCase: false,
          value: resourceType,
        },
      ],
    };
  }, [resourceType]);

  const [nextBtnState, setNextBtnState] = useState(false);
  const [selectedFlowFromLibrary, setSelectedFlowFromLibrary] = useState<any[]>([]);
  const [customQuery, setCustomQuery] = useState(getQuery());
  const { spaceUsers, userDetails } = useSelector((state: RootState) => state);
  const [userSelectionMode, setUserSelectionMode] = useState("currentUser");

  useEffect(() => {
    setCustomQuery(getQuery());
  }, [getQuery]);

  useEffect(() => {
    if (spaceId) {
      dispatch(getAllUsersInSpace({ spaceId }));
    }
  }, [spaceId]);

  const gridRef = useRef<any>(null);

  const handleCreateFromLibrary = async () => {
    if (selectedFlowFromLibrary && selectedFlowFromLibrary.length) {
      let usersData = spaceUsers.map((user: any) => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
      }));

      if (userSelectionMode == "currentUser") {
        usersData = [
          {
            id: userDetails.id,
            name: `${userDetails.firstName} ${userDetails.lastName}`,
          },
        ];
      }
      const formFlowIds = selectedFlowFromLibrary.map((obj: any) => obj._id);

      const res = await formFlowController({
        action: "addFromLibrary",
        formFlowIds,
        usersData,
        spaceId,
        projectId,
      });
      let toastContent: any = "";
      if (res.data.status) {
        toastContent = {
          content: t("Selected Form Flows have been added successfully"),
          type: TOAST_TYPE_SUCCESS,
        };
        refreshGrid();
        handleCancelibrary();
      } else {
        toastContent = {
          content: res.data.errorMessage,
          type: TOAST_TYPE_ERROR,
        };
      }
      dispatch(showToastNotification(toastContent));
    }
  };

  const handleNextClick = () => {
    const selectedFlows = gridRef.current.slickGrid
      .getSelectedRows()
      .map((data: any) => gridRef.current.slickGrid.getDataItem(data));
    setSelectedFlowFromLibrary(selectedFlows);
    setAddFromLibrary((prev: any) => ({ ...prev, step: 2 }));
  };

  const handleUserSelectionModeChange = (args: any) => {
    setUserSelectionMode(args.target.value);
  };

  const content = () => {
    if (addFromLibrary.step == 1) {
      return (
        <FormFlows
          ref={gridRef}
          setNextBtnState={setNextBtnState}
          customService={getEntitesFromLibraryUrl}
          showOnlyGrid={true}
          customQuery={customQuery}
        />
      );
    } else {
      return getUserSelection(
        "Choose who you want to assign in the Form Flow.",
        userSelectionMode,
        handleUserSelectionModeChange,
      );
    }
  };

  const header = () => (
    <div className="flex w-full items-center">
      <div className="flex justify-between w-full items-center">
        <div className="items-center flex gap-1">
          {addFromLibrary.step == 2 && (
            <IconComponent
              onClick={() => setAddFromLibrary((prev: any) => ({ ...prev, step: 1 }))}
              name="BackwardArrow"
              width="1rem"
              height="1rem"
              className="cursor-pointer"
            />
          )}
          {t("Create from Library")}
        </div>
        {addFromLibrary.step === 1 && (
          <SelectSpaces
            moduleName={defaultModulesMapper.CONTRACTS}
            data-testid="spaceDropdownField"
            projectId={projectId}
            selectedSpaceCallback={(params: { _id: string }) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    </div>
  );

  const footer = () => {
    if (addFromLibrary.step == 1) {
      return (
        <div className="flex w-full items-center gap-5">
          <ButtonComponent
            disabled={!nextBtnState || !spaceId}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleNextClick}
          >
            {t("Next")}
          </ButtonComponent>
          <ButtonComponent onClick={handleCancelibrary}>{t("Cancel")}</ButtonComponent>
        </div>
      );
    } else {
      return (
        <div className="flex w-full pt-5 items-center gap-5">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleCreateFromLibrary}>
            {t("Create")}
          </ButtonComponent>
          <ButtonComponent onClick={handleCancelibrary}>{t("Cancel")}</ButtonComponent>
        </div>
      );
    }
  };

  return (
    <ModalStateDialogComponent
      allowModalPadding={true}
      height="70%"
      footerContent={footer()}
      width="80%"
      headerTitle={header()}
      onCancel={handleCancelibrary}
      isVisible={true}
      modelContent={content()}
    />
  );
};
export default FormFlowLibraryPopup;
